/**
 * Tool Sorcerer site helpers.
 * - Every add to cart carries the size's stock pill as a line item property ("Availability: 7-10 Days
 *   Dispatch"), so the cart, checkout, order email and admin order all show when it dispatches.
 * - The cart drawer and cart page show progress to free delivery and the order's dispatch estimate
 *   (the slowest item decides it).
 * - Sizes whose stock is counted are checked against the basket (sections/ts-cart-stock.liquid): asking
 *   for more than is in stock relabels the line, e.g. "1 In Stock, 2 at 7-10 Days Dispatch", and the
 *   dispatch estimate follows the slower part.
 * - The contact form fills its message from ?enquiry= (the finder's "Ask us" links).
 * Words, labels and the free delivery threshold come from window.tsSite in layout/theme.liquid.
 */
(() => {
  const config = window.tsSite || {};
  const text = config.text || {};
  const labels = config.labels || {};
  const property = config.property || 'Availability';
  // Stock pill states, quickest first
  const STATES = ['in-stock', 'order-in', 'long-lead'];

  // 1. Stock pill on every add to cart. new FormData(form) and a normal form submit both fire
  //    "formdata" on the form; listening in the capture phase catches it from any product form.
  document.addEventListener(
    'formdata',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
      const id = event.formData.get('id');
      if (!id) return;
      const scope = form.closest('.shopify-section, product-info, .quick-add-modal') || document;
      const badge = scope.querySelector('[data-stock-badge][data-variants]') || document.querySelector('[data-stock-badge][data-variants]');
      if (!badge) return;
      let map = {};
      try {
        map = JSON.parse(badge.dataset.variants || '{}');
      } catch (error) {
        return;
      }
      const label = labels[map[id]];
      if (label) event.formData.set(`properties[${property}]`, label);
    },
    true
  );

  // 2. Cart note: free delivery progress and dispatch estimate
  const money = (cents) => {
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: config.currency || 'GBP' }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  };

  const noteHtml = (cart, states) => {
    if (!cart.item_count) return '';
    let html = '';
    const threshold = config.freeThreshold || 0;
    if (threshold) {
      const left = threshold - cart.total_price;
      const percent = Math.max(0, Math.min(100, Math.round((cart.total_price / threshold) * 100)));
      html +=
        `<div class="ts-cart-note__free${left <= 0 ? ' is-done' : ''}">` +
        `<p>${left > 0 ? (text.free_left || '').replace('[amount]', money(left)) : text.free_done || ''}</p>` +
        `<span class="ts-cart-note__bar" aria-hidden="true"><span style="width: ${percent}%"></span></span></div>`;
    }
    const ranks = cart.items
      .map((item, i) => {
        const known = states && states[i] && states[i].state;
        if (known) return STATES.indexOf(known);
        return STATES.findIndex((state) => labels[state] && labels[state] === (item.properties || {})[property]);
      })
      .filter((rank) => rank >= 0);
    if (ranks.length) {
      const slowest = STATES[Math.max(...ranks)];
      html += `<p class="ts-cart-note__dispatch ts-cart-note__dispatch--${slowest}">${text[`dispatch_${slowest.replace('-', '_')}`] || ''}</p>`;
      if (slowest !== 'in-stock' && ranks.includes(0) && text.split_tip) {
        html += `<p class="ts-cart-note__tip">${text.split_tip}</p>`;
      }
    }
    return html;
  };

  const spots = () => [...document.querySelectorAll('cart-drawer .drawer__footer, #main-cart-footer .cart__blocks')];

  const root = () => window.Shopify?.routes?.root || '/';

  const getCart = async () => {
    try {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
      return await response.json();
    } catch (error) {
      return null;
    }
  };

  // { variantId: { stock, fallback } } for counted sizes in the cart that keep selling at 0
  const getStock = async () => {
    try {
      const response = await fetch(`${root()}?sections=ts-cart-stock&_=${Date.now()}`, { headers: { Accept: 'application/json' } });
      const html = (await response.json())['ts-cart-stock'] || '';
      const match = html.match(/<script[^>]*data-ts-cart-stock[^>]*>([\s\S]*?)<\/script>/);
      return match ? JSON.parse(match[1]) : {};
    } catch (error) {
      return {};
    }
  };

  // Each line's real state and the label it should carry. Lines of the same size share its stock in order.
  const lineStates = (cart, stock) => {
    const left = {};
    Object.keys(stock).forEach((id) => {
      left[id] = Math.max(0, Number(stock[id].stock) || 0);
    });
    return cart.items.map((item) => {
      const info = stock[item.variant_id];
      if (!info || !labels[info.fallback]) return { state: null, label: null };
      const now = Math.min(item.quantity, left[item.variant_id]);
      left[item.variant_id] -= now;
      const later = item.quantity - now;
      if (!later) return { state: 'in-stock', label: labels['in-stock'] };
      if (!now) return { state: info.fallback, label: labels[info.fallback] };
      const label = (text.split_line || '[now] [now_label], [later] at [later_label]')
        .replace('[now]', now)
        .replace('[now_label]', labels['in-stock'])
        .replace('[later]', later)
        .replace('[later_label]', labels[info.fallback]);
      return { state: info.fallback, label };
    });
  };

  const setLabel = async (line, item, label) => {
    try {
      const response = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity: item.quantity, properties: { ...(item.properties || {}), [property]: label } }),
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  // Redraw the drawer and cart page so the corrected labels show on the lines
  const redraw = () => {
    document.querySelectorAll('cart-drawer-items, cart-items').forEach((el) => {
      if (typeof el.onCartUpdate === 'function') el.onCartUpdate();
    });
  };

  let timer;
  let busy = false;
  let again = false;
  const run = async () => {
    let cart = null;
    let states = [];
    let changed = false;
    // One label at a time, re-reading the cart after each change (line keys change with their properties)
    for (let round = 0; round < 8; round += 1) {
      cart = await getCart();
      if (!cart) return;
      const stock = cart.item_count ? await getStock() : {};
      states = lineStates(cart, stock);
      const index = states.findIndex((s, i) => s.label && (cart.items[i].properties || {})[property] !== s.label);
      if (index < 0) break;
      if (!(await setLabel(index + 1, cart.items[index], states[index].label))) break;
      changed = true;
    }
    if (changed) {
      redraw();
      return;
    }

    const html = noteHtml(cart, states);
    spots().forEach((target) => {
      let note = target.querySelector(':scope > .ts-cart-note');
      if (!html) {
        note?.remove();
        return;
      }
      if (!note) {
        note = document.createElement('div');
        note.className = 'ts-cart-note';
        target.prepend(note);
      }
      if (note.innerHTML !== html) note.innerHTML = html;
    });
  };

  const refresh = () => {
    clearTimeout(timer);
    timer = setTimeout(async () => {
      if (busy) {
        again = true;
        return;
      }
      busy = true;
      try {
        await run();
      } finally {
        busy = false;
        if (again) {
          again = false;
          refresh();
        }
      }
    }, 120);
  };

  // The drawer and cart page re-render themselves on every change; watching them catches all of it
  const watch = () => {
    const observer = new MutationObserver((records) => {
      if (records.every((record) => record.target.closest?.('.ts-cart-note'))) return;
      refresh();
    });
    document.querySelectorAll('cart-drawer, #main-cart-footer, #main-cart-items').forEach((el) => {
      observer.observe(el, { childList: true, subtree: true });
    });
    refresh();
  };

  // 3. Contact form message from the finder's "Ask us" link
  const prefill = () => {
    const enquiry = new URLSearchParams(window.location.search).get('enquiry');
    if (!enquiry) return;
    const box = document.querySelector('textarea[name="contact[body]"]');
    if (box && !box.value.trim()) box.value = enquiry;
  };

  const start = () => {
    watch();
    prefill();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
